import { isDefined } from "@clipboard-health/util-ts";
import { BottomSheet, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useQueryClient } from "@tanstack/react-query";

import { useToast } from "../lib";
import { logError } from "../lib/analytics";
import { useFooterData } from "../redesign/Shift/BottomSheet/useFooterData";
import { invalidateShiftModal } from "../redesign/Shift/Open/useGetShiftModal";
import { type ModalShiftData } from "../redesign/Shift/types";
import { SHIFT_DISCOVERY_SHIFT_MODAL_PATH } from "../redesign/ShiftDiscovery/paths";
import { useEndNegotiation } from "./api/useEndNegotiation";
import { CancelRateNegotiationDialogContent } from "./CancelRateNegotiationDialogContent";
import { CancelRateNegotiationDialogFooter } from "./CancelRateNegotiationDialogFooter";

interface RateNegotiationBottomSheetProps {
  shift: ModalShiftData;
  modalState: UseModalState;
}

export function CancelRateNegotiationBottomSheet(props: RateNegotiationBottomSheetProps) {
  const { shift, modalState } = props;

  const queryClient = useQueryClient();

  const { showSuccessToast, showErrorToast } = useToast();

  const { mutateAsync: endNegotiation, isLoading: isEndingNegotiation } = useEndNegotiation();

  const { navigateToModal } = useFooterData();

  const handleEndNegotiation = async () => {
    try {
      const negotiationId = shift?.rateNegotiation?.id;

      if (!negotiationId) {
        throw new Error("Negotiation ID is required");
      }

      await endNegotiation(negotiationId);

      showSuccessToast("You have successfully ended the negotiation");
      navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
        pathParams: { shiftId: shift.id },
        clearExistingSearchParams: true,
      });
      void invalidateShiftModal(queryClient, shift.id);
    } catch (error) {
      logError("Error Ending Negotiation", { error: JSON.stringify((error as Error)?.stack) });
      showErrorToast("Error Ending Negotiation");
    }
  };

  if (!isDefined(shift)) {
    return null;
  }

  return (
    <BottomSheet modalState={modalState}>
      <CancelRateNegotiationDialogContent />
      <CancelRateNegotiationDialogFooter
        handleEndNegotiation={handleEndNegotiation}
        isEndingNegotiation={isEndingNegotiation}
      />
    </BottomSheet>
  );
}
